import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Play, Pause, SkipBack, SkipForward, Volume2, VolumeX, RotateCcw, HardHat, Building2, ArrowRight } from 'lucide-react';
import { useI18n } from '@/i18n/LanguageContext';
import {
  TUTORIAL_STEPS,
  QUICK_STEPS,
  TUTORIAL_CTAS,
  TUTORIAL_PATH,
  TutorialAudience,
  TutorialStepId,
  bi,
  stepNarration,
} from '@/data/tutorial';
import TutorialScene from '@/components/tutorial/TutorialScene';
import { track } from '@/lib/tracking';

/**
 * "HOW GIGGONE WORKS" PLAYER.
 *
 * Behaves like a short explainer video — chapters, a scrubbable progress bar,
 * captions, play / pause and optional spoken narration (browser speech
 * synthesis in the visitor's language) — but every frame is rendered from
 * src/data/tutorial.ts, so it never goes out of date when prices or wording
 * change, and it works in EN and ES without re-recording anything.
 *
 * `quick` = the 30-second version (4 beats × 7.5s) for homepage visitors.
 * Both versions end on a CTA panel: Create Account / Browse Leads /
 * View Pricing / Join Membership.
 */

interface Props {
  defaultAudience?: TutorialAudience;
  /** Hide the audience toggle (e.g. dashboard already knows the role). */
  lockAudience?: boolean;
  autoPlay?: boolean;
  /** 30-second version. */
  quick?: boolean;
  /** Where the player is embedded — sent with analytics events. */
  placement?: string;
  className?: string;
}

interface Frame {
  id: TutorialStepId;
  title: string;
  text: string;
  where?: string;
  label: string;
}

const msFor = (text: string, quick: boolean) => (quick ? 7500 : Math.max(6500, Math.min(15000, text.length * 55)));

const TutorialPlayer: React.FC<Props> = ({
  defaultAudience = 'sub',
  lockAudience,
  autoPlay = false,
  quick = false,
  placement = 'page',
  className = '',
}) => {
  const { lang, path } = useI18n();
  const [audience, setAudience] = useState<TutorialAudience>(defaultAudience);
  const [i, setI] = useState(0);
  const [playing, setPlaying] = useState(autoPlay);
  const [voice, setVoice] = useState(false);
  const [progress, setProgress] = useState(0);
  const [ended, setEnded] = useState(false);
  const started = useRef(false);
  const tick = useRef<number | null>(null);

  const frames: Frame[] = quick
    ? QUICK_STEPS.map((q) => {
        const full = TUTORIAL_STEPS.find((s) => s.id === q.id)!;
        return { id: q.id, title: bi(full.title, lang), text: bi(q.caption, lang), label: bi(full.label, lang) };
      })
    : TUTORIAL_STEPS.map((s) => ({
        id: s.id,
        title: bi(s.title, lang),
        text: stepNarration(s, audience, lang),
        where: bi(s.where, lang),
        label: bi(s.label, lang),
      }));

  const total = frames.length;
  const step = frames[Math.min(i, total - 1)];
  const text = step.text;
  const duration = msFor(text, quick);
  const canSpeak = typeof window !== 'undefined' && 'speechSynthesis' in window;
  const es = lang === 'es';

  useEffect(() => setAudience(defaultAudience), [defaultAudience]);

  const stopSpeech = () => {
    if (canSpeak) window.speechSynthesis.cancel();
  };

  const go = useCallback(
    (n: number) => {
      stopSpeech();
      setEnded(false);
      setProgress(0);
      setI(Math.max(0, Math.min(total - 1, n)));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [total]
  );

  const next = useCallback(() => {
    if (i >= total - 1) {
      stopSpeech();
      setPlaying(false);
      setEnded(true);
      setProgress(1);
      track('cta_click', { action: quick ? 'tutorial_quick_complete' : 'tutorial_complete', placement, audience });
      return;
    }
    go(i + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i, go, placement, audience, total, quick]);

  // Timer-driven playback (captions only).
  useEffect(() => {
    if (!playing || voice) return;
    const startAt = Date.now() - progress * duration;
    tick.current = window.setInterval(() => {
      const p = (Date.now() - startAt) / duration;
      if (p >= 1) {
        if (tick.current) window.clearInterval(tick.current);
        next();
      } else setProgress(p);
    }, 100);
    return () => {
      if (tick.current) window.clearInterval(tick.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playing, voice, i, audience, lang]);

  // Voice-driven playback: advance when the narration finishes.
  useEffect(() => {
    if (!playing || !voice || !canSpeak) return;
    stopSpeech();
    const u = new SpeechSynthesisUtterance(text);
    u.lang = es ? 'es-US' : 'en-US';
    u.rate = quick ? 1.12 : 1.02;
    const startAt = Date.now();
    const est = text.length * 65;
    const id = window.setInterval(() => setProgress(Math.min(0.97, (Date.now() - startAt) / est)), 150);
    u.onend = () => {
      window.clearInterval(id);
      next();
    };
    window.speechSynthesis.speak(u);
    return () => {
      window.clearInterval(id);
      u.onend = null;
      stopSpeech();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playing, voice, i, audience, lang]);

  useEffect(() => () => stopSpeech(), []); // eslint-disable-line react-hooks/exhaustive-deps

  const togglePlay = () => {
    if (ended) {
      go(0);
      setPlaying(true);
      return;
    }
    if (!started.current) {
      started.current = true;
      track('cta_click', { action: quick ? 'tutorial_quick_play' : 'tutorial_play', placement, audience });
    }
    if (playing) stopSpeech();
    setPlaying((p) => !p);
  };

  const ctrl = 'inline-flex h-10 w-10 items-center justify-center rounded-full text-white transition-colors hover:bg-white/15';

  return (
    <div className={`overflow-hidden rounded-3xl bg-slate-900 shadow-2xl ring-1 ring-slate-800 ${className}`}>
      {/* Header: title + audience toggle */}
      <div className="flex flex-col gap-3 border-b border-white/10 px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-6">
        <p className="text-sm font-extrabold text-white">
          {es ? 'Cómo funciona GIGGONE' : 'How GIGGONE Works'}
          <span className="ml-2 rounded-full bg-orange-500/20 px-2 py-0.5 text-[10px] font-black uppercase tracking-wide text-orange-300">
            {quick ? (es ? 'Versión de 30 s' : '30-second version') : `Tutorial · ${total} ${es ? 'pasos' : 'steps'}`}
          </span>
        </p>
        {!lockAudience && !quick && (
          <div role="tablist" className="inline-flex rounded-xl bg-white/10 p-1">
            {([
              ['sub', HardHat, es ? 'Subcontratistas' : 'Subcontractors'],
              ['gc', Building2, es ? 'Contratistas Generales' : 'General Contractors'],
            ] as const).map(([key, Icon, label]) => (
              <button
                key={key}
                role="tab"
                type="button"
                aria-selected={audience === key}
                onClick={() => {
                  setAudience(key);
                  go(0);
                }}
                className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-bold transition-colors ${
                  audience === key ? 'bg-white text-slate-900' : 'text-slate-300 hover:text-white'
                }`}
              >
                <Icon className="h-3.5 w-3.5" /> {label}
              </button>
            ))}
          </div>
        )}
        {quick && (
          <Link to={path(TUTORIAL_PATH)} className="inline-flex items-center gap-1 text-xs font-bold text-orange-300 hover:text-orange-200">
            {es ? 'Ver tutorial completo (2 min)' : 'Watch the full 2-min tutorial'} <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        )}
      </div>

      {/* Stage */}
      <div className={`relative grid items-center gap-6 bg-gradient-to-br from-slate-900 via-slate-800 to-orange-900/40 px-4 py-8 sm:px-8 lg:grid-cols-2 ${quick ? 'min-h-[360px]' : 'min-h-[420px]'}`}>
        <div key={`${step.id}-${audience}-${lang}`} className="animate-fade-in">
          <p className="text-xs font-black uppercase tracking-widest text-orange-400">
            {es ? 'Paso' : 'Step'} {i + 1} / {total}
          </p>
          <h3 className="mt-2 text-2xl font-extrabold leading-tight text-white sm:text-3xl">{step.title}</h3>
          <p className="mt-4 text-base leading-relaxed text-slate-200" aria-live="polite">{text}</p>
          {step.where && (
            <p className="mt-4 inline-flex flex-wrap items-center gap-2 rounded-lg bg-white/10 px-3 py-2 text-xs font-bold text-orange-200">
              {es ? 'Dónde:' : 'Where:'} <span className="text-white">{step.where}</span>
            </p>
          )}
        </div>
        <div key={`scene-${step.id}-${audience}`} className="flex justify-center animate-fade-in">
          <TutorialScene id={step.id} lang={lang} gc={audience === 'gc'} />
        </div>

        {!playing && !ended && progress === 0 && i === 0 && (
          <button
            type="button"
            onClick={togglePlay}
            className="absolute inset-0 flex items-center justify-center bg-slate-900/40 backdrop-blur-[1px] transition-colors hover:bg-slate-900/30"
            aria-label={es ? 'Reproducir tutorial' : 'Play tutorial'}
          >
            <span className="flex items-center gap-3 rounded-full bg-orange-500 px-6 py-4 text-base font-extrabold text-white shadow-2xl">
              <Play className="h-6 w-6 fill-white" />
              {quick ? (es ? 'Ver en 30 segundos' : 'Watch in 30 seconds') : es ? 'Ver el tutorial (2 min)' : 'Watch the tutorial (2 min)'}
            </span>
          </button>
        )}

        {/* END CARD — conversion CTAs */}
        {ended && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-5 bg-slate-900/90 px-4 text-center backdrop-blur-sm animate-fade-in">
            <p className="text-xs font-black uppercase tracking-widest text-orange-400">{es ? '¡Listo!' : "You're ready"}</p>
            <h3 className="max-w-lg text-2xl font-extrabold text-white sm:text-3xl">
              {es ? 'Empiece a ganar trabajos hoy' : 'Start winning jobs today'}
            </h3>
            <div className="grid w-full max-w-xl grid-cols-1 gap-3 sm:grid-cols-2">
              {TUTORIAL_CTAS.map((c) => (
                <Link
                  key={c.key}
                  to={path(c.path)}
                  onClick={() => track('cta_click', { action: `tutorial_end_${c.key}`, placement, audience, quick })}
                  className={`inline-flex items-center justify-center gap-2 rounded-xl px-5 py-3 text-sm font-extrabold transition-colors ${
                    c.primary ? 'bg-orange-500 text-white hover:bg-orange-600' : 'bg-white text-slate-900 hover:bg-slate-100'
                  }`}
                >
                  {bi(c.label, lang)} <ArrowRight className="h-4 w-4" />
                </Link>
              ))}
            </div>
            <button type="button" onClick={togglePlay} className="inline-flex items-center gap-1.5 text-xs font-bold text-slate-300 hover:text-white">
              <RotateCcw className="h-3.5 w-3.5" /> {es ? 'Ver de nuevo' : 'Watch again'}
            </button>
          </div>
        )}
      </div>

      {/* Chapter progress */}
      <div className="flex gap-1 px-4 pt-3 sm:px-6">
        {frames.map((s, idx) => (
          <button
            key={s.id}
            type="button"
            onClick={() => go(idx)}
            title={s.label}
            aria-label={s.label}
            className="group h-2 flex-1 overflow-hidden rounded-full bg-white/15"
          >
            <span
              className="block h-full rounded-full bg-orange-500 transition-[width] duration-100"
              style={{ width: ended || idx < i ? '100%' : idx === i ? `${progress * 100}%` : '0%' }}
            />
          </button>
        ))}
      </div>

      {/* Controls */}
      <div className="flex items-center justify-between gap-2 px-3 py-2 sm:px-5">
        <div className="flex items-center gap-1">
          <button type="button" className={ctrl} onClick={() => go(i - 1)} aria-label={es ? 'Anterior' : 'Previous'}>
            <SkipBack className="h-5 w-5" />
          </button>
          <button type="button" className={`${ctrl} bg-orange-500 hover:bg-orange-600`} onClick={togglePlay} aria-label={playing ? 'Pause' : 'Play'}>
            {ended ? <RotateCcw className="h-5 w-5" /> : playing ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5 fill-white" />}
          </button>
          <button type="button" className={ctrl} onClick={() => (i < total - 1 ? go(i + 1) : next())} aria-label={es ? 'Siguiente' : 'Next'}>
            <SkipForward className="h-5 w-5" />
          </button>
          {canSpeak && (
            <button
              type="button"
              className={`${ctrl} ${voice ? 'bg-white/15' : ''}`}
              onClick={() => {
                stopSpeech();
                setProgress(0);
                setVoice((v) => !v);
              }}
              aria-label={voice ? (es ? 'Silenciar narración' : 'Mute narration') : es ? 'Activar narración' : 'Turn on narration'}
              title={voice ? (es ? 'Narración activada' : 'Narration on') : es ? 'Narración desactivada' : 'Narration off'}
            >
              {voice ? <Volume2 className="h-5 w-5" /> : <VolumeX className="h-5 w-5" />}
            </button>
          )}
        </div>
        <p className="truncate text-xs font-semibold text-slate-400">{step.label}</p>
      </div>
    </div>
  );
};

export default TutorialPlayer;
